import { SxProps, Theme } from "@mui/material";

// The value column's geometry, shared by the three things that can sit in it:
// either series' figure, and the state that says a row has none. Extracted
// rather than written a third time -- the two figure styles below differ from
// each other in nothing but colour, so a third copy of the same four
// properties is where one of them silently drifts from the others.
const barValueBase = {
  minWidth: 70,
  textAlign: "right",
  fontSize: "0.875rem",
  fontFamily: '"Poppins", sans-serif',
} as const;

// Common style objects for DrawdownComparisonChart
export const drawdownComparisonChartStyles = {
  // Card container styles
  card: {
    padding: 2,
    cursor: "pointer",
    backgroundColor: "white",
    borderRadius: 2,
    border: "1px solid #E0E0E0",
    "&:hover": {
      boxShadow: 4,
    },
  } as SxProps<Theme>,

  // The same card before a report has run: nothing to edit, so it neither
  // points nor lifts on hover.
  cardReadOnly: {
    padding: 2,
    backgroundColor: "white",
    borderRadius: 2,
    border: "1px solid #E0E0E0",
  } as SxProps<Theme>,

  // Title container styles
  titleContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
  } as SxProps<Theme>,

  // Title line 1 styles
  titleLine1: {
    color: "#2C8BB0",
    fontSize: "1rem",
    fontWeight: 500,
    fontFamily: '"Poppins", sans-serif',
    lineHeight: 1.2,
    marginBottom: 0,
  } as SxProps<Theme>,

  // Title line 2 styles
  titleLine2: {
    color: "#2C8BB0",
    fontSize: "1rem",
    fontWeight: 500,
    fontFamily: '"Poppins", sans-serif',
    lineHeight: 1.2,
    marginTop: 0,
  } as SxProps<Theme>,

  // Bars container styles
  barsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  } as SxProps<Theme>,

  // Bar row container styles
  barRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: 1.5,
  } as SxProps<Theme>,

  // Bar label styles
  barLabel: {
    minWidth: 80,
    color: "#003057",
    fontSize: "0.875rem",
    fontFamily: '"Poppins", sans-serif',
    fontWeight: 500,
  } as SxProps<Theme>,

  // Bar container styles (unfilled background) - highly rounded
  barContainer: {
    flex: 1,
    height: 24,
    backgroundColor: "#F5F5F5",
    borderRadius: "12px", // Highly rounded for pill shape
    position: "relative",
    overflow: "hidden",
    marginRight: 2,
  } as SxProps<Theme>,

  // Bar Stock filled portion styles - highly rounded
  barStockFilled: {
    position: "absolute",
    left: 0,
    top: 0,
    height: "100%",
    backgroundColor: "#2C8BB0",
    borderRadius: "12px", // Highly rounded for pill shape on all sides
    borderTopRightRadius: "12px",
    borderBottomRightRadius: "12px",
    transition: "width 0.3s ease",
  } as SxProps<Theme>,

  // Bar OEA filled portion styles - highly rounded
  barOeaFilled: {
    position: "absolute",
    left: 0,
    top: 0,
    height: "100%",
    backgroundColor: "#003057",
    borderRadius: "12px", // Highly rounded for pill shape on all sides
    borderTopRightRadius: "12px",
    borderBottomRightRadius: "12px",
    transition: "width 0.3s ease",
  } as SxProps<Theme>,

  // Bar Stock value styles
  barStockValue: {
    ...barValueBase,
    color: "#2C8BB0",
    fontWeight: 500,
  } as SxProps<Theme>,

  // Bar OEA value styles
  barOeaValue: {
    ...barValueBase,
    color: "#003057",
    fontWeight: 500,
  } as SxProps<Theme>,

  // A row with no figure. It keeps the column's geometry above and REPLACES
  // the figure styles rather than composing with either -- one complete
  // object, because a state's colour and weight are the same whichever
  // series the row is, so there is nothing series-specific left to keep.
  //
  // Muted and unweighted for the rule the card's own fields follow
  // (reportFieldStates): every figure drawn here is one of the two series
  // colours at weight 500, so a state left in either would look exactly
  // like a result.
  //
  // nowrap, and then a type size that makes nowrap SAFE -- both halves are
  // needed and measuring is what showed why.
  //
  // Wrapping is the thing to prevent: this column is 70px, so a wrapped
  // state breaks mid-phrase into an unreadable two-line "No / Report", which
  // is the epic-#482 failure this whole change exists to stop. But nowrap
  // alone only moves the problem -- at the figures' 0.875rem, "No Report"
  // measures 69.8px in that 70px column, so it fits by 0.2px and any font
  // fallback, any zoom, or one more character spills it out over the card's
  // edge instead.
  //
  // So the state drops a step below the figure, which buys ~6px of headroom
  // at every width. That is the same trade reportFieldStates makes for the
  // seeds in the fields themselves -- a capitalised state phrase reads as a
  // state without needing the figure's size, where the figure needs every
  // bit of its own.
  barStateValue: {
    ...barValueBase,
    color: "text.secondary",
    fontWeight: 400,
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,
};

// Modal styles
export const drawdownComparisonEditModalStyles = {
  // Modal container styles
  modalContainer: {
    width: "500px",
  } as SxProps<Theme>,

  // Form buttons container styles
  buttonsContainer: {
    display: "flex",
    gap: 1,
    margin: 1,
  } as SxProps<Theme>,

  // Button styles
  button: {
    flex: 1,
  } as SxProps<Theme>,
};
